import { Label } from '@/components/ui/label';
import { HelpCircle, Building2, Plus, Copy , AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import type { ObrigatoriedadeConfig } from '@/hooks/useObrigatoriedadesItem';
import { GestorCessionarioEditDialog } from '@/components/carteira/GestorCessionarioEditDialog';

// Tipo para dados do gestor que podem ser copiados
interface GestorData {
  id: string;
  nome: string;
  telefone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  cpf?: string | null;
  rg?: string | null;
}

interface Props {
  config: ObrigatoriedadeConfig;
  value: string | null;
  onChange: (value: string) => void;
  clienteId: string;
  erro?: string;
  obrigatorio?: boolean;
  gestorData?: GestorData | null; //  Dados do gestor para copiar
  onCompletarGestor?: () => void;
}

export function CessionarioField({ config, value, onChange, clienteId, erro, obrigatorio = false, gestorData, onCompletarGestor }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mostrarFormNovo, setMostrarFormNovo] = useState(false);
  const [criando, setCriando] = useState(false);
  const [copiandoGestor, setCopiandoGestor] = useState(false);

  const { data: cessionarios } = useQuery({
    queryKey: ['cliente-cessionarios', clienteId],
    queryFn: async () => {
      const { data } = await supabase
        .from('cliente_cessionario')
        .select('id, nome_completo, ordem')
        .eq('cliente_id', clienteId)
        .order('ordem', { ascending: true });
      return data || [];
    },
    enabled: !!clienteId,
  });

  const isValid = !erro && !!value;

  // 🆕 Verificar se gestor tem dados completos para copiar
  const gestorTemDadosCompletos = gestorData && gestorData.nome && gestorData.cpf && gestorData.rg;
  const faltaNoGestor = gestorData
    ? [!gestorData.cpf?.trim() && 'CPF', !gestorData.rg?.trim() && 'RG'].filter((c): c is string => !!c)
    : [];
  const esperandoGestor = !value && faltaNoGestor.length > 0;

  // 🆕 Função para criar NOVO cessionário com dados do gestor (não atualiza existente)
  const handleUsarDadosGestor = async () => {
    if (!gestorData) return;
    
    setCopiandoGestor(true);
    try {
      // Calcular próxima ordem disponível
      const maxOrdem = cessionarios?.length ? Math.max(...cessionarios.map(c => c.ordem ?? 0)) : -1;
      const novaOrdem = maxOrdem + 1;
      
      const dadosCessionario = {
        cliente_id: clienteId,
        nome_completo: gestorData.nome,
        telefone: gestorData.telefone || null,
        whatsapp: gestorData.whatsapp || null,
        email: gestorData.email || null,
        cpf: gestorData.cpf || null,
        rg: gestorData.rg || null,
        ordem: novaOrdem,
        originado_do_gestor_id: gestorData.id,
      };
      
      // Sempre criar NOVO cessionário
      const { data: novoCess, error } = await supabase
        .from('cliente_cessionario')
        .insert([dadosCessionario])
        .select('id')
        .single();
      
      if (error) throw error;
      
      await queryClient.invalidateQueries({
        queryKey: ['cliente-cessionarios', clienteId]
      });
      
      onChange(novoCess.id);
      
      toast({
        title: 'Cedente criado!',
        description: `Cedente "${gestorData.nome}" criado a partir do Gestor.`,
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar cedente',
        description: error.message,
      });
    } finally {
      setCopiandoGestor(false);
    }
  };

  const handleCriarNovo = async (dados: any) => {
    setCriando(true);
    try {
      // Calcular próxima ordem disponível (SEMPRE criar novo, nunca atualizar)
      const maxOrdem = cessionarios?.length ? Math.max(...cessionarios.map(c => c.ordem ?? 0)) : -1;
      const novaOrdem = maxOrdem + 1;
      
      const dadosCessionario = {
        cliente_id: clienteId,
        nome_completo: dados.nome_completo,
        telefone: dados.telefone || null,
        whatsapp: dados.whatsapp || null,
        email: dados.email || null,
        cpf: dados.cpf || null,
        rg: dados.rg || null,
        ordem: novaOrdem,
      };
      
      // Sempre criar NOVO cessionário
      const { data: novoCess, error } = await supabase
        .from('cliente_cessionario')
        .insert([dadosCessionario])
        .select('id')
        .single();
      
      if (error) throw error;
      
      await queryClient.invalidateQueries({
        queryKey: ['cliente-cessionarios', clienteId]
      });
      
      onChange(novoCess.id);
      setMostrarFormNovo(false);
      
      toast({
        title: 'Cedente criado!',
        description: 'O novo cedente foi cadastrado com sucesso.',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar cedente',
        description: error.message,
      });
    } finally {
      setCriando(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <Label className="font-medium">
          {/* Override: sempre mostrar "Cedente" independente do catálogo */}
          Cedente {obrigatorio && '*'}
        </Label>
        {config.mensagem_ajuda && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <HelpCircle className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{config.mensagem_ajuda}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isValid && obrigatorio && (
          <span className="text-xs text-success">✓</span>
        )}
      </div>

      {esperandoGestor && (
        <div className="space-y-1.5 rounded-lg border border-dashed border-primary/60 p-2.5 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-primary">
            <Lock className="h-3.5 w-3.5" />
            Primeiro complete o Gestor
          </div>
          <p className="text-muted-foreground">
            O cedente pode usar os dados do Gestor, mas para isso o Gestor precisa ter CPF e RG. Falta {faltaNoGestor.join(' e ')}.
          </p>
          <p className="text-muted-foreground/80">Cedente é outra pessoa? Use + Novo cedente.</p>
          <div className="flex flex-wrap gap-2 pt-0.5">
            {onCompletarGestor && (
              <Button type="button" size="sm" className="h-7 px-2.5" onClick={onCompletarGestor}>
                Completar {faltaNoGestor.join(' e ')}
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" className="h-7 px-2.5" onClick={() => setMostrarFormNovo(true)}>
              <Plus className="mr-1 h-3 w-3" />
              Novo cedente
            </Button>
          </div>
        </div>
      )}

      {(!esperandoGestor || (cessionarios?.length ?? 0) > 0) && (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className={erro ? 'border-destructive' : isValid ? 'border-success' : ''}>
          <SelectValue placeholder="Selecione o cedente..." />
        </SelectTrigger>
        <SelectContent>
          {/*  Botão para copiar dados do gestor */}
          {gestorData && (
            <div className="p-2 border-b">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      size="sm" 
                      variant={gestorTemDadosCompletos ? "default" : "outline"}
                      className="w-full justify-start gap-2"
                      disabled={!gestorTemDadosCompletos || copiandoGestor}
                      onClick={(e) => {
                        e.preventDefault();
                        handleUsarDadosGestor();
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      {copiandoGestor ? 'Copiando...': `Usar dados do Gestor (${gestorData.nome})`}
                    </Button>
                  </TooltipTrigger>
                  {!gestorTemDadosCompletos && (
                    <TooltipContent>
                      <p className="text-xs">Complete os dados do Gestor primeiro (CPF e RG obrigatórios)</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          
          <div className="p-2 border-b">
            <Button 
              size="sm" 
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={(e) => {
                e.preventDefault();
                setMostrarFormNovo(true);
              }}
            >
              <Plus className="h-4 w-4" />
              + Novo Cedente
            </Button>
          </div>
          {cessionarios && cessionarios.length > 0 ? (
            cessionarios.map((cess) => (
              <SelectItem key={cess.id} value={cess.id}>
                {cess.nome_completo}
              </SelectItem>
            ))
          ) : (
            <div className="p-3 text-center text-sm text-muted-foreground">
              <p><AlertTriangle className="mr-1.5 inline h-3 w-3" />Nenhum cedente cadastrado ainda</p>
            </div>
          )}
        </SelectContent>
      </Select>
      )}

      <GestorCessionarioEditDialog
        open={mostrarFormNovo}
        onOpenChange={setMostrarFormNovo}
        tipo="cessionario"
        dadosIniciais={null}
        onSave={handleCriarNovo}
      />

      {!obrigatorio && !value && (
        <p className="text-xs text-muted-foreground">
           Opcional agora - será obrigatório ao fechar o pedido
        </p>
      )}

      {erro && obrigatorio && (
        <p className="text-xs text-destructive">
          {config.mensagem_erro || erro}
        </p>
      )}
    </div>
  );
}
